import type { OnboardingTemplate, OnboardingTemplateStep } from 'src/interfaces/onboardingTemplate'

/** Trims API / editor config group ids so grouping compares equal to template `collectDataTemplateStepsConfigs[].id`. */
export function normalizeCollectDataTemplateConfigId(id: string | undefined | null): string {
  if (id === null || id === undefined) {
    return ''
  }

  return String(id).trim()
}

export function defaultConfigId(template: OnboardingTemplate): string {
  for (const c of template.collectDataTemplateStepsConfigs ?? []) {
    const id = normalizeCollectDataTemplateConfigId(c.id)

    if (id !== '') {
      return id
    }
  }

  return ''
}

export function resolvedStepConfigId(
  step: OnboardingTemplateStep,
  fallbackWhenMissing: string,
): string {
  const fromStep = normalizeCollectDataTemplateConfigId(step.collectDataTemplateStepsConfigId)

  if (fromStep === '') {
    return fallbackWhenMissing
  }

  return fromStep
}

/** Config group id for a step row (matches `applyEditorStepsToTemplate` fallback). */
export function resolveStepGroupConfigId(
  step: OnboardingTemplateStep,
  template: OnboardingTemplate,
): string {
  return resolvedStepConfigId(step, defaultConfigId(template))
}

/** True when the config group id is client-only (not in the loaded template until save). */
export function isFlowEditorNewConfigId(configId: string, template: OnboardingTemplate): boolean {
  const normalized = normalizeCollectDataTemplateConfigId(configId) || configId

  return !(template.collectDataTemplateStepsConfigs ?? []).some(
    (stepsConfig) =>
      (normalizeCollectDataTemplateConfigId(stepsConfig.id) || stepsConfig.id) === normalized,
  )
}

/** Client id for a new config group (not in GET template until save succeeds). */
export function mintNewFlowEditorConfigId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }

  return `new-cfg-${Date.now()}-${Math.random().toString(16).slice(2)}`
}
